/** Waive one add-on period charge with a required reason (TMMPDM-0019). */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy, isValidationFailed, validationFields } from '@/api/errors';

const REASON_MAX = 500;

interface WaiveChargeDialogProps {
  open: boolean;
  slug: string;
  addonId: number;
  chargeId: number;
  onClose: () => void;
  onDone: () => void;
}

export function WaiveChargeDialog({
  open,
  slug,
  addonId,
  chargeId,
  onClose,
  onDone,
}: WaiveChargeDialogProps) {
  const { t } = useCopy();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('addons.waive.title', 'Waive charge')}</DialogTitle>
      {open ? (
        <WaiveChargeForm
          slug={slug}
          addonId={addonId}
          chargeId={chargeId}
          onClose={onClose}
          onDone={onDone}
        />
      ) : null}
    </Dialog>
  );
}

function WaiveChargeForm({
  slug,
  addonId,
  chargeId,
  onClose,
  onDone,
}: Omit<WaiveChargeDialogProps, 'open'>) {
  const { t } = useCopy();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setFieldError(t('addons.waive.reasonRequired', 'Reason is required.'));
      return;
    }
    if (trimmed.length > REASON_MAX) {
      setFieldError(t('addons.form.tooLong', 'At most {{max}} characters.', { max: REASON_MAX }));
      return;
    }
    setSubmitting(true);
    setError(null);
    setFieldError(null);
    try {
      await adminApi.waiveAddonCharge(slug, addonId, chargeId, { reason: trimmed });
      onDone();
    } catch (err: unknown) {
      if (isValidationFailed(err)) {
        setFieldError(validationFields(err, t).reason ?? errorCopy(err, t, ''));
      } else {
        setError(errorCopy(err, t, t('addons.actionError', 'Could not update the add-on.')));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label={t('addons.waive.reason', 'Reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={Boolean(fieldError)}
            helperText={fieldError}
            required
            multiline
            slotProps={{ htmlInput: { maxLength: REASON_MAX } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('addons.actions.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {t('addons.waive.submit', 'Waive')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
